import { skipToken, useQuery } from '@tanstack/react-query';
import type { SetupGuideEnvironmentDataResponse } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

/**
 * Live workspace data counts (`GET /v1/environments/{id}/setup_guide/integration_status`).
 * "Setup guide" names the wire resource, not the product feature — this is a
 * plain read of how many attributes, people, and events a workspace has received.
 */

/** The three counts, as the API sends them (`attributes` / `customers` / `events`). */
export type IntegrationStatus = SetupGuideEnvironmentDataResponse;

export const integrationStatusQueryKey = (workspaceId: number | null) =>
  ['setupGuide', 'integrationStatus', workspaceId] as const;

/** @public */
export async function getIntegrationStatus(workspaceId: number, signal?: AbortSignal): Promise<IntegrationStatus> {
  return await hydraUiApi.setupGuide.getIntegrationStatus({
    params: { path: { environment_id: String(workspaceId) } },
    signal,
  });
}

type UseIntegrationStatusOptionalOptions = {
  workspaceId: number | null;
  enabled?: boolean;
};

/**
 * Fetches without suspending; disabled until a workspace id is known. Counts
 * refresh on remount/staleness only — the app QueryClient disables focus
 * refetching.
 */
export function useIntegrationStatusOptional({ workspaceId, enabled = true }: UseIntegrationStatusOptionalOptions) {
  return useQuery({
    queryKey: integrationStatusQueryKey(workspaceId),
    queryFn: enabled && workspaceId != null ? ({ signal }) => getIntegrationStatus(workspaceId, signal) : skipToken,
  });
}
